import React, { Suspense, useCallback } from 'react'
import { EmojiPickerButton } from '@components/EmojiPickerButton'
import { EmojiSpan } from '@components/EmojiSpan'
import { MessageReactions } from '@towns-protocol/sdk'
import { Pill } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useHandleReaction } from 'hooks/useReactions'
import { TooltipBox as Box } from 'ui/components/Box/TooltipBox'
import { Stack } from 'ui/components/Stack/Stack'
import { Text } from 'ui/components/Text/Text'

import { getNativeEmojiFromName } from './ReactionConstants'
import { ReactionTootip } from './ReactionTooltip'

type Props = {
    reactions: MessageReactions
    userId?: string | null
    parentId?: string
    onReaction?: ReturnType<typeof useHandleReaction>
    tipReaction?: React.JSX.Element
}

export const Reactions = React.memo(function Reactions(props: Props) {
    const { userId, parentId, reactions, onReaction, tipReaction } = props

    const onReactionPicker = useCallback(
        (data: EmojiPickerSelection) => {
            if (onReaction && parentId && data.id) {
                onReaction({
                    type: 'add',
                    parentId,
                    reactionName: data.id,
                })
            }
        },
        [onReaction, parentId],
    )

    const { isTouch } = useDevice()

    return (
        <Stack horizontal flexWrap="wrap" gap={{ default: 'xs', mobile: 'sm' }}>
            <Suspense>
                <ReactionRow
                    userId={userId}
                    parentId={parentId}
                    reactions={reactions}
                    tipReaction={tipReaction}
                    onReaction={onReaction}
                />
            </Suspense>
            {onReaction ? (
                <EmojiPickerButton
                    pill
                    size={isTouch ? 'square_sm' : 'square_xs'}
                    onSelectEmoji={onReactionPicker}
                />
            ) : (
                <></>
            )}
        </Stack>
    )
})

const ReactionRow = ({
    reactions,
    userId,
    onReaction,
    parentId,
    tipReaction,
}: {
    reactions: MessageReactions
    userId?: Props['userId']
    onReaction?: Props['onReaction']
    parentId?: Props['parentId']
    tipReaction?: React.JSX.Element
}) => {
    const onReact = useCallback(
        (reactionName: string, remove: boolean) => {
            if (onReaction && parentId && userId) {
                if (remove) {
                    const eventId = reactions[reactionName]?.[userId]?.eventId
                    if (eventId) {
                        onReaction({
                            type: 'redact',
                            eventId,
                        })
                    }
                } else {
                    onReaction({
                        type: 'add',
                        parentId,
                        reactionName,
                    })
                }
            }
        },
        [onReaction, parentId, reactions, userId],
    )

    const entries = Object.entries<Record<string, { eventId: string }>>(reactions)
    const map = entries.length
        ? entries.map(([reaction, users]) => (
              <Reaction
                  key={reaction}
                  name={reaction}
                  users={users}
                  isOwn={!!(userId && users[userId])}
                  onReact={onReaction ? onReact : undefined}
              />
          ))
        : undefined
    return <>{[tipReaction, ...(map ? [map] : [])]}</>
}

const Reaction = (props: {
    name: string
    users?: Record<string, { eventId: string }>
    onReact?: (reaction: string, remove: boolean) => void
    isOwn?: boolean
}) => {
    const { name, users, onReact, isOwn } = props

    const onClick = useCallback(() => {
        const remove = !!isOwn
        onReact?.(name, remove)
    }, [isOwn, name, onReact])

    return users && Object.keys(users).length ? (
        <Box horizontal tooltip={<ReactionTootip userIds={users} reaction={name} />}>
            <Pill
                horizontal
                centerContent
                position="relative"
                rounded="lg"
                gap="xs"
                background={isOwn ? 'level3' : undefined}
                border={isOwn ? 'textDefault' : undefined}
                onClick={onReact ? onClick : undefined}
            >
                <EmojiSpan
                    emoji={getNativeEmojiFromName(props.name)}
                    size="md"
                    fontSize={{ desktop: 'mds', mobile: 'xs' }}
                />
                <Text fontWeight="medium" size="sm">
                    {Object.keys(users).length}
                </Text>
            </Pill>
        </Box>
    ) : null
}
